import { useRef, useCallback } from 'react';
import { useGameStore } from '@hollowveil/shared';
import './TouchControls.css';

export function TouchControls() {
  const { updateInput, switchWorld, currentRoom, currentWorld, showJoystick } = useGameStore();
  const joystickRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  const touchIdRef = useRef<number | null>(null);
  const centerRef = useRef({ x: 0, y: 0 });

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (touchIdRef.current !== null) return;

    const touch = e.touches[0];
    touchIdRef.current = touch.identifier;

    if (joystickRef.current) {
      const rect = joystickRef.current.getBoundingClientRect();
      centerRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = Array.from(e.touches).find(
      (t) => t.identifier === touchIdRef.current
    );
    if (!touch || !knobRef.current) return;

    const maxDistance = 40;
    const dx = touch.clientX - centerRef.current.x;
    const dy = touch.clientY - centerRef.current.y;
    const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const angle = Math.atan2(dy, dx);

    const knobX = Math.cos(angle) * distance;
    const knobY = Math.sin(angle) * distance;

    knobRef.current.style.transform = `translate(calc(-50% + ${knobX}px), calc(-50% + ${knobY}px))`;

    // Normalize to -1 to 1
    const normalizedX = knobX / maxDistance;
    const normalizedY = -knobY / maxDistance;

    updateInput({
      movement: { x: normalizedX, y: normalizedY },
    });
  }, [updateInput]);

  const handleTouchEnd = useCallback(() => {
    touchIdRef.current = null;
    if (knobRef.current) {
      knobRef.current.style.transform = 'translate(-50%, -50%)';
    }
    updateInput({ movement: { x: 0, y: 0 } });
  }, [updateInput]);

  const handleWorldSwitch = useCallback(() => {
    if (currentRoom?.hasSpectralLayer) {
      switchWorld();
    }
  }, [switchWorld, currentRoom]);

  return (
    <div className="touch-controls">
      {/* Joystick area - can be hidden via settings */}
      {showJoystick && (
        <div
          className="joystick-container"
          ref={joystickRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <div className="joystick-base">
            <div className="joystick-knob" ref={knobRef} />
          </div>
        </div>
      )}

      {/* World switch button - always visible on mobile */}
      {currentRoom?.hasSpectralLayer && (
        <button
          className={`world-switch-button ${currentWorld}`}
          onClick={handleWorldSwitch}
          aria-label="Changer de monde"
        >
          <span className="world-icon">
            {currentWorld === 'real' ? '☀️' : '🌙'}
          </span>
          <span className="world-label">
            {currentWorld === 'real' ? 'Spectral' : 'Réel'}
          </span>
        </button>
      )}

      {/* Help text */}
      <div className="touch-help">
        Joystick pour bouger • Tap objets pour interagir
      </div>
    </div>
  );
}
